import 'react-datepicker/dist/react-datepicker.css';
import 'react-dropdown-now/style.css';
import React, { useEffect, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert'; // Import
import DataTable from 'react-data-table-component';
import Tooltip from 'react-simple-tooltip';

import axios from 'axios';

import { useGlobalContext } from '../../AppContext';
import { VestingCalendarObject } from '../../constants/interfaces';
import {
  add_vesting_calendar,
  remove_vesting_calendar,
  update_vesting_calendar,
} from '../../utils/apiOperations';
import {
  calculateColumnWidth,
  fetchTokenDetails,
  parseAmount,
  parseMoney,
} from '../../utils/utils';
import { BigButton } from '../../components/BigButton';
import { BigMenu } from '../../components/BigMenu';
import { Button } from '../../components/Button';
import { ConfirmAlert } from '../../components/ConfirmAlert';
import { TextField } from '../../components/TextField';

import './VestingCalendar.scss';

export const VestingCalendar = () => {
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const handleThemeChange = () => {
      const newTheme = document.body.classList.contains('dark') ? 'light' : 'dark';
      setTheme(newTheme);
    };

    handleThemeChange();

    const observer = new MutationObserver(handleThemeChange);
    const config = { attributes: true, attributeFilter: ['class'] };
    observer.observe(document.body, config);

    return () => {
      observer.disconnect();
    };
  }, []);

  const { config, vestingCalendars, setVestingCalendars } = useGlobalContext();
  const [showTable, setShowTable] = useState<JSX.Element>(<></>);
  const [tableData, setTableData] = useState<TableDataProps[]>([]);
  const [rawData, setRawData] = useState<any>(false);
  const [vestingTime, setVestingTime] = useState<number>(0);
  const [step, setStep] = useState<number>(1);

  const [selectedVestingCalender, setSelectedVestingCalender] = useState<VestingCalendarObject>({});

  const [newVestingCalendar, setNewVestingCalendar] = useState<VestingCalendarObject>({});

  const [selectedType, setSelectedType] = useState<string>('list');
  const [, setContractAddressCheck] = useState<boolean>(true);

  useEffect(() => {
    if (config && vestingCalendars) {
      setRawData(vestingCalendars);
    } else {
      renderShowTable([]);
    }
  }, [config, vestingCalendars]);

  useEffect(() => {
    renderShowTable(tableData);
  }, [tableData]);

  const renderShowTable = (data: any): void => {
    setShowTable(
      <DataTable
        noDataComponent={
          <div className="data-table-no-record">
            <h1>Vesting Calendar</h1>
            <hr />
            <div className="light-button-container">
              <div className="light-button">
                <Button
                  onClick={() => setSelectedType('add')}
                  color="one"
                  size="large"
                  label="Add New Vesting"
                />
              </div>
            </div>
          </div>
        }
        pagination
        columns={columns}
        data={data}
      />,
    );
  };

  const columns = [
    {
      name: 'Project',
      selector: (row: TableDataProps) => row.name,
      cell: (row: TableDataProps) => row.name,
      width: calculateColumnWidth('vestingCalendar', 'name'),
    },
    {
      name: 'Symbol',
      selector: (row: TableDataProps) => row.symbol,
      cell: (row: TableDataProps) => row.symbol,
      width: calculateColumnWidth('vestingCalendar', 'symbol'),
    },
    {
      name: 'TGE Price',
      selector: (row: TableDataProps) => row.priceRaw,
      width: calculateColumnWidth('vestingCalendar', 'price'),
      cell: (row: TableDataProps) => row.price,
    },
    {
      name: 'Total Supply',
      selector: (row: TableDataProps) => row.totalSupply,
      cell: (row: TableDataProps) => row.totalSupply,
      width: calculateColumnWidth('vestingCalendar', 'totalSupply'),
    },
    {
      name: 'Next Release',
      selector: (row: TableDataProps) => row.nextActionTime,
      cell: (row: TableDataProps) => row.nextActionTime,
      width: calculateColumnWidth('vestingCalendar', 'initialMarketcap'),
    },
    {
      name: 'Sale Type',
      selector: (row: TableDataProps) => row.saleType,
      cell: (row: TableDataProps) => row.saleType,
      width: calculateColumnWidth('vestingCalendar', 'saleType'),
    },
    {
      name: 'Distribution',
      selector: (row: TableDataProps) => row.distribution,
      cell: (row: TableDataProps) => row.distribution,
      width: calculateColumnWidth('vestingCalendar', 'distribution'),
    },

    {
      name: '+',
      selector: (row: TableDataProps) => row.button,
      width: calculateColumnWidth('vestingCalendar', 'action'),
      cell: (row: TableDataProps) => {
        return (
          <div className="table-actions">
            <Tooltip
              placement="left"
              className="tooltip"
              border="#ff6aff"
              background="var(--color-background-main)"
              color="var(--color-text-main)"
              content={`Update your vesting details`}
            >
              <img
                onClick={() => {
                  setSelectedType('edit');
                  setSelectedVestingCalender(
                    Object.values(vestingCalendars).filter((item: any) => item.id === row.id)[0],
                  );
                }}
                className=""
                src={`/icons/edit-2-${theme}.svg`}
                alt=""
              />
            </Tooltip>

            <img
              onClick={() =>
                confirmAlert({
                  closeOnClickOutside: false,
                  customUI: ({ onClose }) => {
                    return (
                      <ConfirmAlert title="Warning!" onClose={onClose}>
                        <p className="info">
                          You are going to delete your vesting details from the list. This action
                          cannot be undone.
                          <br />
                          <strong>Are you sure you want to delete this?</strong>
                        </p>
                        <div className="confirm-button-flex">
                          <BigButton type="light" size="small" onClick={onClose} name="No" />
                          <BigButton
                            type="dark"
                            size="small"
                            onClick={() => {
                              remove_vesting_calendar(row.id, () => {
                                setVestingCalendars('');
                                setRawData({});
                              });
                              onClose();
                            }}
                            name="Yes, Delete it!"
                          />
                        </div>
                      </ConfirmAlert>
                    );
                  },
                })
              }
              src={`/icons/trash-${theme}.svg`}
              alt=""
            />
          </div>
        );
      },
    },
  ];

  interface TableDataProps {
    name: string;
    symbol: string;
    totalSupply: string;
    initialMarketcap: string;
    saleType: string;
    distribution: string;
    price: string;
    priceRaw: number;
    marketcap: string;
    marketcapRaw: number;
    button: boolean;
  }

  const showFormattedDate = (date: any) => {
    const nextActionTime = new Date(date);
    const year = nextActionTime.getFullYear();
    let month = nextActionTime.getMonth() + 1;
    let dt = nextActionTime.getDate();

    if (dt < 10) {
      dt = '0' + dt;
    }
    if (month < 10) {
      month = '0' + month;
    }

    return `${month}/${dt}/${year}`;
  };

  useEffect(() => {
    const tmpData: TableDataProps[] = new Array();
    Object.values(rawData).forEach((item: any) => {
      const nextActionTime = new Date(item.nextActionTime);
      const year = nextActionTime.getFullYear();
      let month = nextActionTime.getMonth() + 1;
      let dt = nextActionTime.getDate();

      if (dt < 10) {
        dt = '0' + dt;
      }
      if (month < 10) {
        month = '0' + month;
      }

      tmpData.push({
        id: item.id,
        name: item.projectName,
        symbol: item.symbol,
        saleType: item.saleType,
        totalSupply: parseAmount(item.totalSupply),
        initialMarketcap: parseAmount(item.initialMarketcap),
        distribution: (
          <>
            {parseAmount(item.totalDistributed)} / {parseAmount(item.purchaseAmount)}
          </>
        ),
        price: parseMoney(item.salePrice),
        priceRaw: parseFloat(item.salePrice),
        marketcap: parseAmount(item.initialMarketcap),
        marketcapRaw: parseFloat(item.initialMarketcap),
        nextActionTime: item.nextActionTime ? `${month}/${dt}/${year}` : '-',
        button: true,
      });
    });
    setTableData(tmpData);
  }, [rawData]);

  useEffect(() => {
    //
    if (selectedType === 'edit') {
      setNewVestingCalendar({
        ...selectedVestingCalender,
        tgeDate:
          typeof selectedVestingCalender.tgeDate === 'object'
            ? selectedVestingCalender.tgeDate
            : new Date(selectedVestingCalender.tgeDate),
        months: JSON.parse(selectedVestingCalender!.months || '{}'),
        monthsLocked: JSON.parse(selectedVestingCalender!.monthsLocked || '{}'),
      });
    } else {
      setNewVestingCalendar({
        ...selectedVestingCalender,
        months: JSON.parse(selectedVestingCalender!.months || '{}'),
        monthsLocked: JSON.parse(selectedVestingCalender!.monthsLocked || '{}'),
      });
    }

    setVestingTime(selectedVestingCalender.vestingTime);
    if (newVestingCalendar.tokenContract) {
      setContractAddressCheck(true);
    }
  }, [selectedVestingCalender]);

  const handleOnVestingTimeChange = (e: any) => {
    setNewVestingCalendar({
      ...newVestingCalendar,
      vestingTime: e > 36 ? 36 : e > 0 ? e : 0,
    });

    setVestingTime(e > 36 ? 36 : e > 0 ? e : 0);
  };

  const getTokenDetails = async (contractAddress: string) => {
    let response: any;
    try {
      response = await axios({
        url: `${process.env.REACT_APP_API}/fetchTokenDetails.php?address=${contractAddress}`,
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      });
    } catch (error) {
      // console.log
    }

    if (response && response.data) {
      setNewVestingCalendar({
        ...newVestingCalendar,
        projectName: response.data.name ? response.data.name : newVestingCalendar.projectName,
        symbol: response.data.symbol ? response.data.symbol : newVestingCalendar.symbol,
        totalSupply: response.data.supply
          ? Math.round(response.data.supply)
          : newVestingCalendar.totalSupply,
        initialMarketcap: response.data.marketcap
          ? Math.round(response.data.marketcap)
          : newVestingCalendar.initialMarketcap,
        website: response.data.website ? response.data.website : newVestingCalendar.website,
        twitter: response.data.twitter ? response.data.twitter : newVestingCalendar.twitter,
        telegram: response.data.telegram ? response.data.telegram : newVestingCalendar.telegram,
        discord: response.data.discord ? response.data.discord : newVestingCalendar.discord,
      });
    } else {
      if (newVestingCalendar.network && newVestingCalendar.network.value) {
        const contractResult = await fetchTokenDetails(
          contractAddress,
          newVestingCalendar.network.value,
        );

        if (contractResult) {
          setNewVestingCalendar({
            ...newVestingCalendar,
            projectName: contractResult.name ? contractResult.name : newVestingCalendar.projectName,
            symbol: contractResult.symbol ? contractResult.symbol : newVestingCalendar.symbol,
            totalSupply: contractResult.totalSupply
              ? Math.round(contractResult.totalSupply)
              : newVestingCalendar.totalSupply,
          });
        }
      }
    }
  };

  useEffect(() => {
    if (newVestingCalendar.tokenContract && !newVestingCalendar.symbol) {
      getTokenDetails(newVestingCalendar.tokenContract);
    }
  }, [newVestingCalendar.tokenContract, newVestingCalendar.network]);

  useEffect(() => {
    if (selectedType === 'edit') {
      setStep(5);
    }
  }, [selectedType]);

  const vestingTimeBoxes = () => {
    const rows = [];
    for (let i = 0; i < vestingTime; i++) {
      rows.push(
        <div className="form-input" key={i}>
          <TextField
            label={
              newVestingCalendar.tgeDate
                ? showFormattedDate(
                    new Date(newVestingCalendar.tgeDate).setMonth(
                      newVestingCalendar.tgeDate.getMonth() + i,
                    ),
                  )
                : `Month#${i + 1}`
            }
            onChange={(e: any) => {
              /*
              setNewVestingCalendar({
                ...newVestingCalendar,
                months: { ...newVestingCalendar.months, [`month${i + 1}`]: e },
              });
              */

              arrangeVestingPercentage(e, i);
            }}
            onToggleLock={(toggleLockChecked: boolean) => {
              setNewVestingCalendar({
                ...newVestingCalendar,
                monthsLocked: {
                  ...newVestingCalendar.monthsLocked,
                  [`month${i + 1}`]: toggleLockChecked,
                },
              });
            }}
            value={newVestingCalendar.months[`month${i + 1}`]}
            disabled={newVestingCalendar.months[`month${i + 1}`] === 0}
            emptyValue={0}
            toggle={true}
            toggleLock={true}
          />
        </div>,
      );
    }

    return (
      <>
        {rows.length > 0 && <h2>Vesting Details</h2>}
        <div className="flex vestingTime">{rows}</div>
      </>
    );
  };

  useEffect(() => {
    if (newVestingCalendar.vestingPeriod && vestingTime) {
      const vp = newVestingCalendar.vestingPeriod.value;
      const vt = vestingTime;
      let rvp = 1;

      if (vp === 'Once per 1 month') {
        rvp = 1;
      } else if (vp === 'Once per 2 months') {
        rvp = 2;
      } else if (vp === 'Once per 3 months') {
        rvp = 3;
      } else if (vp === 'Once per 4 months') {
        rvp = 4;
      } else if (vp === 'Once per 5 months') {
        rvp = 5;
      } else if (vp === 'Once per 6 months') {
        rvp = 6;
      }

      const averageVesting = (100 / Math.ceil(vt / rvp)).toFixed(2);
      //console.log(averageVesting);
      let months = { ...newVestingCalendar.months };
      let monthsLocked = {};
      for (let i = 0; i < vestingTime; i++) {
        if (i % rvp === 0) months = { ...months, [`month${i + 1}`]: averageVesting };
        else months = { ...months, [`month${i + 1}`]: 0 };

        monthsLocked = { ...monthsLocked, [`month${i + 1}`]: true };
      }
      setNewVestingCalendar({
        ...newVestingCalendar,
        months: { ...months },
        monthsLocked: { ...monthsLocked },
      });
      /*
      setNewVestingCalendar({
        ...newVestingCalendar,
        months: { ...newVestingCalendar.months, [`month${i + 1}`]: e },
      });
      */
    }
  }, [newVestingCalendar.vestingPeriod, newVestingCalendar.purchaseAmount, vestingTime]);

  const arrangeVestingPercentage = (value, month) => {
    //console.log('arrangeVestingPercentage', value, month);
    let months = { ...newVestingCalendar.months };
    months[`month${month + 1}`] = parseFloat(value);
    //console.log(months);
    let staticValues = 0;
    let active = 0;
    Object.keys(newVestingCalendar.monthsLocked).map((key: any) => {
      if (!newVestingCalendar.monthsLocked[key] || parseFloat(months[key]) === 0) {
        staticValues += parseFloat(months[key]);
      } else {
        active++;
      }
    });
    const rest = 100 - staticValues;
    const averageVesting = (rest / Math.ceil(active)).toFixed(2);
    //console.log(active, averageVesting);
    Object.keys(newVestingCalendar.monthsLocked).map((key: any) => {
      if (newVestingCalendar.monthsLocked[key] && parseFloat(months[key]) !== 0) {
        months = { ...months, [key]: averageVesting };
      }
    });

    setNewVestingCalendar({
      ...newVestingCalendar,
      months: { ...months },
    });
  };

  useEffect(() => {
    //console.log(newVestingCalendar);
  }, [newVestingCalendar]);

  return (
    <div className="vesting-calendar">
      <BigMenu horizontal={true} />
      {selectedType === 'list' && (
        <>
          <div className="table-container mt-5">
            <div className={`custom-table-${theme} padding-left`}>
            <div className="table-border-top" />
              {showTable}
            <div className="table-border-bottom" />
            </div>
          </div>
          {vestingCalendars &&
            Object.values(vestingCalendars) &&
            Object.values(vestingCalendars).length > 0 && (
              <div className="mt-5">
                <div className="light-button-container">
                  <div className="light-button">
                    <Button
                      onClick={() => setSelectedType('add')}
                      color="one"
                      size="large"
                      label="Add New Vesting"
                    />
                  </div>
                </div>
              </div>
            )}
        </>
      )}

      {(selectedType === 'add' || selectedType === 'edit') && (
        <>
          <div className="table-container mt-5">
            <div className={`custom-table-${theme} padding-left`}>
              <div className="table-border-top vesting-calendar" />
              <div className="full-form">
                <h1>Add new vesting {step}/5 </h1>
                <p>
                  Please enter your project name. If available, you can directly enter token
                  contract address to retrieve onchain data.
                </p>
                <hr className="separator" />
                <h2>Project Details</h2>
                <div className="flex">
                  <TextField
                    label="Project Name"
                    autoComplete={[]}
                    onChange={(value: any) => {
                      if (value && value.label) {
                        setNewVestingCalendar({
                          ...newVestingCalendar,
                          projectName: value.label,
                          tokenContract: value.name,
                        });
                      }
                    }}
                    value={newVestingCalendar.projectName}
                  />

                  <TextField
                    label="Network"
                    dropdown={[
                      'Ethereum',
                      'Polygon',
                      'Avalanche',
                      'BSC',
                      'Moonbeam',
                      'Moonriver',
                      'Mantle',
                    ]}
                    onChange={(value: any) => {
                      setNewVestingCalendar({
                        ...newVestingCalendar,
                        network: value,
                      });
                    }}
                    value={newVestingCalendar.network}
                  />

                  <TextField
                    label="Contract address"
                    disabled={false}
                    onChange={(e: any) => {
                      setNewVestingCalendar({
                        ...newVestingCalendar,
                        tokenContract: e,
                      });
                    }}
                    value={newVestingCalendar.tokenContract}
                    toggle={true}
                  />
                  <TextField
                    label="Symbol"
                    onChange={(e: any) => {
                      setNewVestingCalendar({
                        ...newVestingCalendar,
                        symbol: e,
                      });
                    }}
                    value={newVestingCalendar.symbol}
                  />
                </div>

                {[2, 3, 4, 5].includes(step) && (
                  <div className="flex">
                    <TextField
                      label="Sale Type"
                      dropdown={['Private', 'Seed', 'Public', 'Others']}
                      onChange={(value: any) => {
                        setNewVestingCalendar({
                          ...newVestingCalendar,
                          saleType: value,
                        });
                      }}
                      value={newVestingCalendar.saleType}
                    />
                    <TextField
                      label="Sale Price (USD)"
                      onChange={(e: any) => {
                        setNewVestingCalendar({
                          ...newVestingCalendar,
                          purchaseUSD: '0',
                          purchaseAmount: '0',
                          salePrice: e,
                        });
                      }}
                      type="number"
                      value={newVestingCalendar.salePrice}
                    />
                    <TextField
                      label="Purchase (Amount)"
                      onChange={(e: any) => {
                        setNewVestingCalendar({
                          ...newVestingCalendar,
                          purchaseUSD:
                            parseFloat(newVestingCalendar.salePrice) > 0
                              ? ((newVestingCalendar.salePrice * e * 100) / 100).toFixed(2)
                              : newVestingCalendar.purchaseUSD,
                          purchaseAmount: e,
                        });
                      }}
                      value={newVestingCalendar.purchaseAmount}
                    />
                    <TextField
                      label="Purchase (USD)"
                      onChange={(e: any) => {
                        setNewVestingCalendar({
                          ...newVestingCalendar,
                          purchaseAmount:
                            parseFloat(newVestingCalendar.salePrice) > 0
                              ? Math.ceil(e / newVestingCalendar.salePrice)
                              : newVestingCalendar.purchaseAmount,
                          purchaseUSD: e,
                        });
                      }}
                      value={newVestingCalendar.purchaseUSD}
                    />
                  </div>
                )}

                {[3, 4, 5].includes(step) && (
                  <>
                    <div className="flex">
                      <TextField
                        label="TGE Date"
                        datepicker={true}
                        toggle={true}
                        disabled={true}
                        onChange={(date: any) => {
                          setNewVestingCalendar({
                            ...newVestingCalendar,
                            tgeDate: date,
                          });
                        }}
                        value={newVestingCalendar.tgeDate}
                      />
                      <TextField
                        label="Vesting Period"
                        dropdown={[
                          'Once per 1 month',
                          'Once per 2 months',
                          'Once per 3 months',
                          'Once per 4 months',
                          'Once per 5 months',
                          'Once per 6 months',
                        ]}
                        onChange={(value: any) => {
                          setNewVestingCalendar({
                            ...newVestingCalendar,
                            vestingPeriod: value,
                          });
                        }}
                        value={newVestingCalendar.vestingPeriod}
                      />
                      <TextField
                        label="Distributed Amount"
                        onChange={(e: any) => {
                          setNewVestingCalendar({
                            ...newVestingCalendar,
                            totalDistributed: e,
                          });
                        }}
                        value={newVestingCalendar.totalDistributed}
                        toggle={true}
                        disabled={true}
                      />
                      <TextField
                        label="Vesting Time (Months)"
                        onChange={handleOnVestingTimeChange}
                        value={vestingTime || newVestingCalendar.vestingTime}
                      />
                    </div>
                    <div className="flex">
                      <TextField
                        label="Distribution amount on TGE"
                        value={newVestingCalendar.tgeDistribution}
                        onChange={(e: any) => {
                          setNewVestingCalendar({
                            ...newVestingCalendar,
                            tgeDistribution: e,
                          });
                        }}
                        toggle={true}
                        disabled={true}
                      />
                      <TextField
                        label="Cliff months after TGE"
                        value={newVestingCalendar.tgeCliff}
                        onChange={(e: any) => {
                          setNewVestingCalendar({
                            ...newVestingCalendar,
                            tgeCliff: e,
                          });
                        }}
                        toggle={true}
                        disabled={true}
                      />
                    </div>
                  </>
                )}

                {[4, 5].includes(step) && (
                  <>
                    <h2>Extra Information</h2>
                    <div className="flex">
                      <TextField
                        label="Website"
                        onChange={(e: any) => {
                          setNewVestingCalendar({
                            ...newVestingCalendar,
                            website: e,
                          });
                        }}
                        value={newVestingCalendar.website}
                      />
                      <TextField
                        label="Twitter"
                        onChange={(e: any) => {
                          setNewVestingCalendar({
                            ...newVestingCalendar,
                            twitter: e,
                          });
                        }}
                        value={newVestingCalendar.twitter}
                      />
                      <TextField
                        label="Telegram"
                        onChange={(e: any) => {
                          setNewVestingCalendar({
                            ...newVestingCalendar,
                            telegram: e,
                          });
                        }}
                        value={newVestingCalendar.telegram}
                      />
                      <TextField
                        label="Discord"
                        onChange={(e: any) => {
                          setNewVestingCalendar({
                            ...newVestingCalendar,
                            discord: e,
                          });
                        }}
                        value={newVestingCalendar.discord}
                      />
                    </div>

                    <div className="flex">
                      <TextField
                        label="Total Supply"
                        onChange={(e: any) => {
                          setNewVestingCalendar({
                            ...newVestingCalendar,
                            totalSupply: e,
                          });
                        }}
                        value={newVestingCalendar.totalSupply}
                      />
                      <TextField
                        label="Initial Supply"
                        onChange={(e: any) => {
                          setNewVestingCalendar({
                            ...newVestingCalendar,
                            initialSupply: e,
                          });
                        }}
                        value={newVestingCalendar.initialSupply}
                      />
                      <TextField
                        label="Initial Marketcap"
                        onChange={(e: any) => {
                          setNewVestingCalendar({
                            ...newVestingCalendar,
                            initialMarketcap: e,
                          });
                        }}
                        value={newVestingCalendar.initialMarketcap}
                      />
                      <TextField
                        label="Fully Diluted Marketcap"
                        onChange={(e: any) => {
                          setNewVestingCalendar({
                            ...newVestingCalendar,
                            fullyDilutedMarketcap: e,
                          });
                        }}
                        value={newVestingCalendar.fullyDilutedMarketcap}
                      />
                    </div>
                  </>
                )}

                {[5].includes(step) && <>{vestingTimeBoxes()}</>}

                <div className="button-container">
                  <BigButton
                    type="dark"
                    className="marginRightAuto"
                    onClick={() => {
                      setSelectedVestingCalender({});
                      setNewVestingCalendar({});
                      setSelectedType('list');
                      setStep(1);
                    }}
                    size="tiny"
                    name="Cancel"
                  />

                  {step >= 1 && step <= 5 && (
                    <>
                      {step > 1 && selectedType === 'add' && (
                        <BigButton
                          type="dark"
                          onClick={() => {
                            setStep(step - 1);
                          }}
                          size="tiny"
                          name="Back"
                        />
                      )}
                      {step < 5 && (
                        <BigButton
                          type="light"
                          onClick={() => {
                            setStep(step + 1);
                          }}
                          disabled={step === 4 && !vestingTime ? true : false}
                          size="tiny"
                          name="Next"
                        />
                      )}
                    </>
                  )}

                  {step === 5 && (
                    <BigButton
                      type="light"
                      onClick={() => {
                        if (selectedType === 'edit')
                          update_vesting_calendar(newVestingCalendar.id, newVestingCalendar, () => {
                            setVestingCalendars('');
                            setRawData({});
                            setTimeout(() => {
                              setSelectedType('list');
                            }, 500);
                          });
                        else
                          add_vesting_calendar(newVestingCalendar, () => {
                            setVestingCalendars('');
                            setRawData({});
                            setTimeout(() => {
                              setSelectedType('list');
                            }, 500);
                          });
                      }}
                      disabled={step === 4 && !vestingTime ? true : false}
                      size="tiny"
                      name="Save"
                    />
                  )}
                </div>
              </div>
              <div className="table-border-bottom vesting-calendar" />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
